import * as React from 'react';
import { useActions } from '../viewModalActions';
import { View } from '../../../types/View';
import { Schema, SchemaField, Workspace } from '../../../types/Metadata';
import {
  cn,
  dndCardDetails,
  dndMove,
  getSchema,
  parseField
} from '../../../utils';
import { ModalGroup, ModalGroups } from '../../ModalGroup/modalGroup';
import { Typeahead } from '../../Typeahead/typeahead';
import { Icon, Trash } from 'rcd-cpp-ui/ui/icon';
import { SixDots } from '../../Icon/icon';
import { SchemaFieldTypeahead } from '../../SchemaFieldTypeahead/schemaFieldTypeahead';
import { FieldType } from '../../../enums/FieldType';
import { NodeType } from '../../../enums/NodeType';
import { useSelector } from '../../../utils/useSelector';
import { removeDuplicates } from 'rcd-cpp-workspace/utils/array';
import { UserSettingProps } from '../../../types/UserSetting';

export const PageDisplay = () => {
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const fields = useSelector(s => s.viewModal?.fields);
  const data = useSelector(s => s.viewModal?.data);
  const parentView = useSelector(s => s.viewModal.parentView);
  const isMyItemsView = useSelector(s => s.viewModal?.isMyItemsView);
  if (!selectedWorkspace) return null;
  const { schemas } = selectedWorkspace;
  const dataType = getSchema(schemas, data);
  const dataTypeName = dataType?.props?.name;
  const { setViewSetting } = useActions();
  return (
    <ModalGroups>
      <ModalGroup label="Display Settings">
        <div>
          <label>Select Data Type</label>
          <Typeahead
            value={dataTypeName}
            options={schemas}
            disabled={isMyItemsView || !!parentView}
            showAllByDefault
            includes={(t, s) =>
              s?.props?.name.toLocaleLowerCase().includes(t?.toLowerCase())
            }
            onSelect={data => {
              setViewSetting({
                data: data.props.type,
                sortBy: [],
                fields: []
              });
            }}
            sort={(a, b) => a?.props?.name.localeCompare(b?.props?.name)}
            renderOption={o => o.props.name}
          />
        </div>
        <PageDisplayFields
          selectedWorkspace={selectedWorkspace}
          data={data}
          fields={fields}
          parentView={parentView}
          setViewSetting={setViewSetting}
          addLabel="Add Display Field"
          rearrangeLabel="Rearrange Display Fields"
        />
      </ModalGroup>
    </ModalGroups>
  );
};

type PageDisplayFieldsProps = {
  selectedWorkspace: Workspace;
  data: NodeType;
  fields: string[];
  displayFields?: string[];
  parentView: View.Setting;
  setViewSetting: (setting: UserSettingProps) => void;
  addLabel: string;
  rearrangeLabel: string;
  disabled?: boolean;
  hideSeeAll?: boolean;
};

export const PageDisplayFields = ({
  selectedWorkspace,
  data,
  disabled,
  hideSeeAll,
  fields,
  displayFields,
  parentView,
  setViewSetting,
  addLabel,
  rearrangeLabel
}: PageDisplayFieldsProps) => {
  const parentFields = parentView?.props?.fields || [];
  const allFields = removeDuplicates([...parentFields, ...(fields || [])]);
  const scrollContainer = React.useRef(null);
  const [showAllFields, setShowAllFields] = React.useState(false);
  const { schemas } = selectedWorkspace;
  const dataType = getSchema(schemas, data);
  const dataTypeName = displayFields?.length
    ? 'Display'
    : dataType?.props?.name;
  return (
    <div>
      <div>
        <label>{addLabel}</label>
        <SchemaFieldTypeahead
          flat
          disabled={!dataType || disabled}
          onSelectField={(field, { setText }) => {
            const updatedFields = fields?.length
              ? fields
                  .filter(f => f != field.key && !parentFields.includes(f))
                  .concat([field.key])
              : [].concat([field.key]);
            setViewSetting({
              fields: updatedFields
            });
            setText('');
          }}
          excludeFields={(f, s) => {
            if (s?.props?.type == NodeType.Comment) return true;
            if (f.key == 'Item.companionBy') return true;
            if (f.key == 'Item.seriesBy') return true;
            if (f.key == 'Item.maintenanceCOEModifiedBy') return true;
            if (f.fieldType == FieldType.custom) return true;
            if (allFields?.includes(f.key)) return true;
            if (!showAllFields) {
              if (displayFields?.length) return !displayFields.includes(f.key);
              else if (s.props.type != data) return true;
            }
            return false;
          }}
        >
          {!hideSeeAll && (
            <div className="typeahead-popular-fields">
              <div>{showAllFields ? 'All' : dataTypeName} Fields</div>
              <div onClick={() => setShowAllFields(!showAllFields)}>
                See {showAllFields ? dataTypeName + ' Fields' : 'All'}
              </div>
            </div>
          )}
        </SchemaFieldTypeahead>
      </div>
      <div ref={scrollContainer} style={{ marginTop: '30px' }}>
        <label>{rearrangeLabel}</label>
        {allFields.map((field: string, index: number) => {
          const { schemaField } = parseField(field, schemas);
          if (!schemaField) return null;
          return (
            <Card
              isDisplayField={parentFields.includes(field)}
              key={field}
              index={index}
              onMove={dndMove(allFields, setViewSetting, true)}
              field={schemaField}
              onDelete={() => {
                const updatedFields = fields?.length
                  ? fields.filter(f => f != field)
                  : [];
                setViewSetting({ fields: updatedFields });
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export const Card = ({
  isDisplayField,
  index,
  field,
  onMove,
  onDelete
}: {
  field: SchemaField;
  isDisplayField: boolean;
  index: number;
  onMove: Function;
  onDelete: () => void;
}) => {
  const { ref, opacity, handlerId } = isDisplayField
    ? { ref: null, opacity: null, handlerId: null }
    : dndCardDetails(onMove, field.key, index);
  return (
    <div className="card-group">
      <div
        className={cn('card', { 'card-disabled': isDisplayField })}
        ref={ref}
        style={{ opacity }}
        data-handler-id={handlerId}
      >
        {field.displayName}
        {!isDisplayField && (
          <div>
            <SixDots />
          </div>
        )}
      </div>
      {!isDisplayField && (
        <Icon onClick={onDelete} disabled={field.field == 'title'}>
          <Trash />
        </Icon>
      )}
    </div>
  );
};

const getCardFields = (card: View.Card, schemas: Schema[]) => {
  const dataSchema = getSchema(schemas, card?.props?.data);
  if (!dataSchema) return [];
  const displayField = dataSchema.props.fields.filter(
    f => f.field == dataSchema.props.displayField
  )[0];
  const fields = [
    { field: displayField.key },
    ...card.props.fields.filter(f => f.field != displayField.key)
  ];
  return fields
    .map(f => parseField(f.field, schemas))
    .filter(x => x?.schemaField);
};
